// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useEffect } from 'react';
import { useShellStore } from '../../helios/stores/shellStore';

/**
 * Puts `data-carbon-theme` on `<html>` from the shared shell store.
 *
 * Helios' own `ThemeEffects` (components/shell/ThemeEffects.tsx) already does
 * this, but it only mounts inside `AppShell` — a direct load of `/admin-center`
 * (deep link, refresh, new tab) never renders it, so the attribute is left
 * unset and every `[data-carbon-theme='...'] .admin-center-root` override in
 * `admin-theme.css` silently no-ops (borders, the sidebar version line, etc.
 * render with no color). Admin Center sets the same attribute itself instead
 * of assuming Helios got there first — safe to run alongside `ThemeEffects`
 * since both write the identical value from the same store.
 */
export function useSyncDocumentTheme(): void {
  const theme = useShellStore(s => s.theme);

  useEffect(() => {
    document.documentElement.setAttribute('data-carbon-theme', theme);

    // The attribute alone only drives Helios' own token layer. Carbon ships
    // its white `--cds-*` set on :root and the dark set under `.cds--g100`, so
    // without the class every @carbon/react component renders light inside a
    // dark shell. ThemeEffects does the same two writes for Helios; both
    // surfaces need them, and writing identical values twice is harmless.
    document.body.classList.toggle('cds--g100', theme === 'g100');
    document.body.classList.toggle('cds--g10', theme === 'g10');
  }, [theme]);
}
